import { Box } from '@mui/material';
import CanvasElement from './CanvasElement';
import { Dispatch, MouseEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import MovementZoneOverlay from './MovementZoneOverlay';
import IEslTemplateItem, { IEslTemplateItemDimensions, IEslTemplateItemPosition } from '../../../models/esl/eslTemplateElement';
import { EEslTemplateElementType } from '../../../models/enums/eslTemplateType';
import IEslTemplate from '../../../models/esl/eslTemplate';
import { getEslSignature } from '../eslSignature';
import { colorForScreen, quantizeImageForScreen } from '../quantizeImage';
import { useEslPreviewMode } from '../EslPreviewModeContext';


interface ICanvasProps {
  imageUrls: Map<string, string>;
  template: IEslTemplate;
  setTemplate: (template: IEslTemplate) => void;
  selectedElementId: number;
  setSelectedElementId: Dispatch<SetStateAction<number>>;
  width: number;
  height: number;
}

const Canvas = ({ imageUrls, template, setTemplate, selectedElementId, setSelectedElementId, width, height }: ICanvasProps) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const { mode } = useEslPreviewMode();

  const sourceSignatureUrl = getEslSignature(template);
  const [signatureUrl, setSignatureUrl] = useState<string>(sourceSignatureUrl);

  useEffect(() => {
    if (mode === 'design') {
      setSignatureUrl(sourceSignatureUrl);
      return;
    }
    let cancelled = false;
    setSignatureUrl(sourceSignatureUrl);
    quantizeImageForScreen(sourceSignatureUrl, mode).then((url) => {
      if (!cancelled) setSignatureUrl(url);
    });
    return () => { cancelled = true; };
  }, [sourceSignatureUrl, mode]);

  const canvasBackground = mode === 'design' ? '#FFFFFF' : colorForScreen('#FFFFFF', mode);

  const onClickInsideCanvas = (event: MouseEvent<HTMLDivElement>) => {
    if ((canvasRef.current && canvasRef.current === event.target)) return setSelectedElementId(0);
    if (canvasContainerRef.current && canvasContainerRef.current === event.target) return setSelectedElementId(0);
  };

  const onDragAndResize = (id: number, position: IEslTemplateItemPosition, size: IEslTemplateItemDimensions) => {
    const updatedElements = template.elements.map(el => {
      if (el.id !== id) return el;

      return {
        ...el,
        position: {
          left: position.left,
          top: position.top,
        },
        dimensions: {
          width: Math.min(size.width, width),
          height: Math.min(size.height, height),
        },
      };
    });

    setTemplate({ ...template, elements: updatedElements });
  };

  const getElementImageUrl = (element: IEslTemplateItem) => {
    if (element.type !== EEslTemplateElementType.Image || !element.imageSrc) return undefined;

    return imageUrls.get(element.imageSrc) || element.imageSrc;
  }

  return (
    <Box ref={canvasContainerRef} sx={{ width: "100%", display: "flex", justifyContent: "center", paddingTop: 4, overflow: "auto" }} onMouseDown={onClickInsideCanvas}>
      <Box
        ref={canvasRef}
        sx={{
          width,
          height,
          position: 'relative',
          border: '1px solid #ccc',
          bgcolor: canvasBackground,
          overflow: 'hidden',
        }}
      >
        {template.elements.map((el) => (
          <CanvasElement
            key={el.id}
            templateItem={{ ...el, imageSrc: getElementImageUrl(el) }}
            onDragAndResize={onDragAndResize}
            selectedElementId={selectedElementId}
            setSelectedElementId={setSelectedElementId}
          />
        ))}
        {template.elements
          .filter((el) => el.type === EEslTemplateElementType.ServiceRequestQRCode)
          .map((el) => (
            <MovementZoneOverlay
              key={`movement-zone-${el.id}`}
              element={el}
              templateWidth={template.width}
              templateHeight={template.height}
            />
          ))}
        <img
          src={signatureUrl}
          style={{ position: 'absolute', bottom: 0, right: 0, height: 28, zIndex: 1 }}
        />
      </Box>
    </Box>
  );
};

export default Canvas;